import LineString from 'ol/geom/LineString'
import { ref } from 'vue'

export function useMapInteractions({ map, mapStore }) {
  const selectedFeature = ref(null)

  /**
   * Enable feature connection for line features
   */
  const enableConnectFeatures = (featureSource, minZoom = 0) => {
    if (!map.value) return

    map.value.on('moveend', () => {
      if (!map.value) return
      const zoom = map.value.getView().getZoom()
      if (!zoom || zoom < minZoom) {
        return
      }
      connectFeatures(featureSource)
    })
  }

  /**
   * Connect features with lines
   */
  const connectFeatures = source => {
    if (!source || !map.value) return

    source.forEachFeatureInExtent(
      map.value.getView().calculateExtent(),
      feature => {
        const geometries = feature.getGeometry().getGeometries()
        if (feature.get('connection') || geometries.length < 2) {
          return
        }
        const lineCoords = geometries.map(geometry => geometry.getCoordinates())
        if (lineCoords.length > 1) {
          const line = new LineString(lineCoords)
          geometries.push(line)
          feature.getGeometry().setGeometries(geometries)
          feature.set('connection', true)
        }
      },
    )
  }

  /**
   * Enable feature selection on click
   */
  const enableClickSelect = () => {
    if (!map.value) return

    map.value.on('click', handleClick)
    map.value.on('pointermove', handlePointerMove)
  }

  const disableClickSelect = () => {
    if (!map.value) return
    map.value.un('click', handleClick)
    map.value.un('pointermove', handlePointerMove)
  }

  const handleClick = evt => {
    if (selectedFeature.value) {
      // Note: Using 'isSelected' instead of '_selected' for WebGL GLSL compatibility
      selectedFeature.value.set('isSelected', false)
      selectedFeature.value = null
    }

    const features = []
    map.value?.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
      if (layer.get('clusters')) {
        feature.set('isSelected', true)
        selectedFeature.value = feature
        features.push(...feature.get('features'))
      } else {
        features.push(feature)
      }
    })

    mapStore.selected = features
  }

  const handlePointerMove = evt => {
    const pixel = map.value?.getEventPixel(evt.originalEvent)
    if (!pixel || !map.value) return

    const hit = map.value.hasFeatureAtPixel(pixel)
    const target = map.value.getTarget()
    if (target && typeof target !== 'string') {
      target.style.cursor = hit ? 'pointer' : ''
    }
  }

  /**
   * Handle mouse move for hover effects
   */
  const move = evt => {
    if (!map.value || evt.dragging) {
      return
    }

    const hitData = []

    // Check hit data on each layer group
    if (map.value.getLayers) {
      map.value.getLayers().forEach(layer => {
        if (layer.getLayers) {
          layer.getLayers().forEach(sublayer => {
            if (sublayer.getData && typeof sublayer.getData === 'function') {
              const data = sublayer.getData(evt.pixel)
              // transparent pixels have zero for data[3]
              if (data && data.length) {
                hitData.push(data[3])
              } else {
                hitData.push(0)
              }
            }
          })
        }
      })
    }

    const hit = hitData.reduce((partialSum, a) => partialSum + a, 0)
    const element = map.value.getTargetElement()
    if (element) {
      element.style.cursor = hit > 0 ? 'pointer' : ''
    }
  }

  /**
   * Enable hover effects
   */
  const enableMouseHover = () => {
    if (!map.value) return
    map.value.on('pointermove', move)
  }

  /**
   * Disable hover effects
   */
  const disableMouseHover = () => {
    if (!map.value) return
    map.value.un('pointermove', move)
  }

  return {
    selectedFeature,
    enableConnectFeatures,
    connectFeatures,
    enableClickSelect,
    disableClickSelect,
    enableMouseHover,
    disableMouseHover,
  }
}
